import { Navigate } from 'react-router-dom';
import { StaffLayout } from '../StaffLayout';
import { useStaffPillars } from '../useStaffPillars';
import { useTodaysPrograms } from '../useTodaysPrograms';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
  /** Virtual classes this teacher holds. `null` means none — they are an explicit grant. */
  virtual_class_keys?: string[] | null;
}

interface Props {
  staffUser: StaffUser;
}

function formatToday(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'short', day: 'numeric',
  });
}

/**
 * The teacher portal's front door. Rather than a menu, it resolves the default roster
 * group among those running TODAY and drops the teacher straight onto it — attendance is
 * the one thing this portal is for, and it should be zero taps from launch.
 *
 * The target comes from the pillars rather than from the program list, because they are not
 * the same set: a teacher who holds only Chinese or Math is scoped to no programs at all,
 * and reading the target off `programs` sent them to "No programs meet today" while their
 * subject sat in the menu beside it. The pillars are already ordered After School first, so
 * the daily program still wins over a once-a-week enrichment class.
 *
 * Falls back to an empty state (not a redirect loop) when nothing meets today.
 */
export function StaffLanding({ staffUser }: Props) {
  const { data: programs, isLoading, today } = useTodaysPrograms(staffUser);
  const { pillars } = useStaffPillars(staffUser);

  if (isLoading || !programs) {
    return (
      <StaffLayout staffUser={staffUser}>
        <div className="space-y-2">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      </StaffLayout>
    );
  }

  const target = pillars[0]?.path ?? null;

  if (!target) {
    return (
      <StaffLayout staffUser={staffUser}>
        <h1 className="text-lg font-bold text-gray-900 mb-1">Attendance</h1>
        <p className="text-sm text-gray-500 mb-4">{formatToday(today)}</p>
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500 text-sm">No programs meet today.</p>
        </div>
      </StaffLayout>
    );
  }

  return <Navigate to={target} replace />;
}
